import type { Item } from './types'

// a language code as path segment: `en`, `de`, `de-AT`, `de_AT`
const LANGUAGE_SEGMENT = /^[a-z]{2}(?:[-_][a-z]{2})?$/i

/**
 * The language of a result page, for `lang` on the result (WCAG 3.1.2). The API may deliver it as
 * `data.language`; otherwise it is read from the first two path segments of the link (`/en/…`, `/advent/de/…`).
 * Null when unknown, so the result inherits the page language.
 */
export function itemLanguage(item: Item): string | null {
    const delivered = item.data.language

    if (typeof delivered === 'string' && delivered !== '') {
        return delivered
    }

    let path: string

    try {
        path = new URL(item.link, 'http://localhost').pathname
    } catch {
        return null
    }

    const segment = path
        .split('/')
        .filter(Boolean)
        .slice(0, 2)
        .find((part) => LANGUAGE_SEGMENT.test(part) && isLanguage(part.replace('_', '-')))

    return segment ? segment.replace('_', '-') : null
}

// Intl.DisplayNames (ES2021) is not in the ES2019 lib this package targets; typed here, feature-detected at runtime
interface LanguageNames {
    of(code: string): string | undefined
}

type LanguageNamesConstructor = new (locales: string[], options: { type: 'language'; fallback: 'none' }) => LanguageNames

let languageNames: LanguageNames | null | undefined

/** True for a language the browser knows (`go`, `at` are none); a wrong `lang` is worse than none. */
function isLanguage(code: string): boolean {
    if (languageNames === undefined) {
        const DisplayNames = (Intl as { DisplayNames?: LanguageNamesConstructor }).DisplayNames
        languageNames = typeof DisplayNames === 'function' ? new DisplayNames(['en'], { type: 'language', fallback: 'none' }) : null
    }

    if (!languageNames) {
        return true
    }

    try {
        return languageNames.of(code) !== undefined
    } catch {
        return false
    }
}
